import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Button, CircularProgress } from "@mui/material";
import { useIntl } from "react-intl";
import RevaText from "components/common/RevaText";
import { useGestionAccess } from "hooks/useGestionAccess";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { GESTION_AREAS } from "utils/gestion-access";
import { GESTION_ROUTES } from "constants/gestion-routes";

/**
 * Renders the page only when the current user holds the given Gestión area.
 * Without an area (or with "any") the content is rendered as before.
 * The API enforces the same rule; this only avoids showing a page full of 403s.
 */
const GestionAreaGuard = ({ area, children }) => {
  const intl = useIntl();
  const { can, loading } = useGestionAccess();
  const { customerId } = useGestionCustomer();

  if (!area || area === GESTION_AREAS.ANY) return children;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (can(area)) return children;

  return (
    <Box data-testid="gestion-area-forbidden" sx={{ width: "100%", maxWidth: 640, mx: "auto", p: { xs: 3, md: 4 } }}>
      <RevaText variant="h4" sx={{ mb: 2 }}>
        {intl.formatMessage({ id: "gestion.access.forbidden.title" })}
      </RevaText>
      <RevaText variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {intl.formatMessage({ id: "gestion.access.forbidden.body" })}
      </RevaText>
      {customerId ? (
        <Button component={RouterLink} to={GESTION_ROUTES.dashboard(customerId)} variant="contained">
          {intl.formatMessage({ id: "gestion.access.forbidden.back" })}
        </Button>
      ) : null}
    </Box>
  );
};

export default GestionAreaGuard;
